/**
 * Singleton WebSocket client for /ws/live.
 *
 * Contract:
 *   - one connection per browser tab
 *   - opens lazily on first subscribe
 *   - authenticates with the bearer token from getIdToken(), offered as
 *     the subprotocol pair ["omni.bearer", token]: browsers cannot set
 *     headers on a WebSocket upgrade, but Sec-WebSocket-Protocol is a
 *     header, so the token stays out of the URL and out of access and
 *     proxy logs. The server refuses ?access_token= (RW-1.22).
 *   - sends/receives JSON envelopes shaped per backend's
 *     OmniEnvelope schema
 *   - on socket close, fires every onClose listener and clears them
 *
 * The hook in useLiveStream.ts wraps this client.
 */
import { WS_URL } from "@/api/baseUrl";

/** Must match ``BEARER_SUBPROTOCOL`` in backend/omni/modules/gateway/ws_routes.py. */
export const BEARER_SUBPROTOCOL = "omni.bearer";

export interface OmniEnvelopeOut {
  type: string;
  session_id: string;
  payload: Record<string, unknown>;
  correlation_id?: string;
}

export interface OmniEnvelopeIn {
  id: string;
  type: string;
  user_id: string;
  session_id: string;
  payload: Record<string, unknown>;
  correlation_id: string;
  causation_id: string | null;
  created_at: string;
}

type Listener = (env: OmniEnvelopeIn) => void;
type CloseListener = (event: CloseEvent) => void;

export interface OmniSocket {
  send: (env: OmniEnvelopeOut) => void;
  onMessage: (fn: Listener) => () => void;
  onOpen: (fn: () => void) => () => void;
  onClose: (fn: CloseListener) => () => void;
  close: () => void;
  /** Close the current socket (without tearing down the singleton or
   *  listeners) so the next send()/ready() transparently reopens it.
   *  Used when a stored preference the backend reads on connect (e.g.
   *  the voice driver/mode) has changed and must be re-applied. */
  reconnect: () => void;
  /** Resolves when the underlying WS reaches OPEN. */
  ready: () => Promise<void>;
}

const URL = WS_URL;

let current: OmniSocket | null = null;

export function getOmniSocket(
  getIdToken: (forceRefresh?: boolean) => Promise<string | null>,
): OmniSocket {
  if (current) return current;

  const listeners = new Set<Listener>();
  const closeListeners = new Set<CloseListener>();
  const openListeners = new Set<() => void>();
  let ws: WebSocket | null = null;
  let opening: Promise<void> | null = null;
  let reconnectAttempts = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let isIntentionallyClosed = false;

  async function open(forceRefresh = false): Promise<void> {
    if (ws && ws.readyState === WebSocket.OPEN) {
      reconnectAttempts = 0;
      return;
    }
    if (opening) return opening;
    opening = (async () => {
      try {
        const token = await getIdToken(forceRefresh);
        if (!token) throw new Error("not authenticated");
        const sock = new WebSocket(URL, [BEARER_SUBPROTOCOL, token]);
        ws = sock;

        sock.addEventListener("message", (ev) => {
          // Text only — live audio travels over LiveKit (Docs/architecture/31).
          if (typeof ev.data !== "string") return;
          let env: OmniEnvelopeIn;
          try {
            env = JSON.parse(ev.data) as OmniEnvelopeIn;
          } catch {
            return;
          }
          listeners.forEach((fn) => fn(env));
        });

        sock.addEventListener("close", (ev) => {
          closeListeners.forEach((fn) => fn(ev));
          if (ws === sock) {
            ws = null;
          }
          if (isIntentionallyClosed) {
            current = null;
            return;
          }
          if (ev.code === 1000) {
            // Graceful close (preference reconnect or server go-away).
            void open().catch(() => {});
            return;
          }
          if (ev.code === 1008) {
            // Authentication/policy violation. This typically means the session token expired.
            // Force a token refresh and reconnect exactly once.
            if (reconnectAttempts < 2) {
              reconnectAttempts++;
              console.warn("[OmniSocket] Closed with 1008 (auth expired). Forcing token refresh and reconnecting...");
              if (reconnectTimer) clearTimeout(reconnectTimer);
              reconnectTimer = setTimeout(() => {
                void open(true).catch(() => {});
              }, 1000);
              return;
            } else {
              console.error("[OmniSocket] Reconnection failed after token refresh. Stopping.");
              current = null;
              return;
            }
          }
          // Abnormal close (e.g. 1006 network drop). Auto-reconnect with exponential backoff.
          const backoff = Math.min(1000 * Math.pow(1.5, reconnectAttempts), 30000);
          reconnectAttempts++;
          console.warn(`[OmniSocket] Closed (${ev.code}). Reconnecting in ${Math.round(backoff)}ms...`);
          if (reconnectTimer) clearTimeout(reconnectTimer);
          reconnectTimer = setTimeout(() => {
            void open().catch(() => {});
          }, backoff);
        });

        await new Promise<void>((resolve, reject) => {
          sock.addEventListener("open", () => {
            reconnectAttempts = 0;
            openListeners.forEach((fn) => fn());
            resolve();
          }, { once: true });
          sock.addEventListener("error", () => reject(new Error("ws error")), {
            once: true,
          });
        });
      } catch (err) {
        if (!isIntentionallyClosed) {
          const backoff = Math.min(1000 * Math.pow(1.5, reconnectAttempts), 30000);
          reconnectAttempts++;
          if (reconnectTimer) clearTimeout(reconnectTimer);
          reconnectTimer = setTimeout(() => {
            void open().catch(() => {});
          }, backoff);
        }
        throw err;
      }
    })();
    try {
      await opening;
    } finally {
      opening = null;
    }
  }

  current = {
    send: (env) => {
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        void open().then(() => {
          if (ws && ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify(env));
          }
        });
        return;
      }
      ws.send(JSON.stringify(env));
    },
    onMessage: (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    onOpen: (fn) => {
      openListeners.add(fn);
      return () => openListeners.delete(fn);
    },
    onClose: (fn) => {
      closeListeners.add(fn);
      return () => closeListeners.delete(fn);
    },
    close: () => {
      isIntentionallyClosed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      ws?.close(1000, "client closed");
      ws = null;
      current = null;
    },
    reconnect: () => {
      // Keep the singleton and all listeners intact; just drop the
      // live socket. The "close" handler reconnects automatically with
      // backoff (1006).
      if (reconnectTimer) clearTimeout(reconnectTimer);
      reconnectAttempts = 0;
      const sock = ws;
      ws = null;
      if (sock && sock.readyState !== WebSocket.CLOSED) {
        sock.close(1000, "reconnecting for updated preference");
      } else {
        void open().catch(() => {});
      }
    },
    ready: open,
  };
  return current;
}

/** Test-only — resets the module-level singleton. */
export function __resetOmniSocketForTests(): void {
  current?.close();
  current = null;
}
